import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVoiceUiStore } from '@/stores/voiceUiStore';
import { useDataStore } from '@/stores/dataStore';
import { useVoiceRoom } from '@/components/contentPane/useVoiceRoom';
import { hasLiveVideoTrack, participantLabel } from '@/components/contentPane/voiceRoomView';
import { useSpeakingMap } from '@/lib/voiceLevels';
import { getDmCall, subscribeDmCall, endActiveCall, type DmCallState } from '@/lib/dmCall';
import { isNativeApp } from '@/lib/isNativeApp';
import type { GuildMemberRow } from '@/types/api';
import { Tooltip } from '@/components/Tooltip';
import { micShownOff } from '@/lib/voiceIndicators';
import {
  MicIcon,
  MicOffIcon,
  HeadphonesIcon,
  HeadphonesOffIcon,
  ScreenShareIcon,
  PhoneOffIcon,
} from '@/components/icons';

/**
 * Floating call window for an active 1:1 / group DM voice call. Mounted once
 * at app-shell level. Shows the participants (with speaking ring + mute/deaf
 * badges), the shared screen if anyone is presenting, a call timer, and the
 * core controls (mic / headphones / screen-share / hang up). Renders nothing
 * unless a DM call is connecting or in progress — guild voice has its own
 * in-channel view.
 */
export function DmCallView() {
  const [call, setCall] = useState<DmCallState>(() => getDmCall());
  useEffect(() => subscribeDmCall(setCall), []);
  if (call.kind !== 'in-call' && call.kind !== 'connecting') return null;
  return <DmCallViewInner channelId={call.channelId} connecting={call.kind === 'connecting'} />;
}

function fmt(ms: number): string {
  const t = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(t / 60);
  const s = t % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function DmCallViewInner({ channelId, connecting }: { channelId: string; connecting: boolean }) {
  const { t } = useTranslation();
  const dmChannel = useDataStore((s) => s.dmChannels[channelId]);

  // Shim the DM participants into the GuildMemberRow shape useVoiceRoom expects
  // for label/avatar resolution (no guild here).
  const members: GuildMemberRow[] = useMemo(
    () =>
      (dmChannel?.directParticipants ?? []).map((p) => ({
        guildId: '',
        userId: p.userId,
        nickname: null,
        joinedAt: p.joinedAt,
        timeoutUntil: null,
        roleIds: [],
        user: {
          id: p.user.id,
          platformId: p.user.platformId,
          username: p.user.username,
          displayName: p.user.displayName,
          avatarUrl: p.user.avatarUrl,
          status: p.user.status,
          statusText: p.user.statusText ?? null,
        },
      })),
    [dmChannel],
  );

  const {
    participants,
    voiceError,
    muted,
    deafened,
    localScreenOn,
    remoteScreenStreamsByUserId,
    membersById,
    toggleMute,
    toggleDeaf,
    toggleScreen,
  } = useVoiceRoom({ channelId, members, roles: [], ownerId: '', voiceChannels: [], userLimit: null });
  // A DM call is 1-2 people, so the whole map is cheap here — but it must be
  // read locally: useVoiceRoom no longer subscribes (it re-rendered the voice
  // grid ~12x/s for everyone).
  const speakingMap = useSpeakingMap();

  // Call timer — counts from when this view mounted (≈ call connect).
  const startRef = useRef<number>(Date.now());
  const [, force] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => force((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Pick a live shared screen to show large (prefer a remote peer's).
  const remoteScreenEntry = [...remoteScreenStreamsByUserId.entries()].find(([, stream]) =>
    hasLiveVideoTrack(stream),
  );
  const screenVideoRef = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const v = screenVideoRef.current;
    if (!v) return;
    const stream = remoteScreenEntry?.[1] ?? null;
    if (v.srcObject !== stream) v.srcObject = stream;
    return () => {
      if (v.srcObject) v.srcObject = null;
    };
  }, [remoteScreenEntry]);

  // Screen sound is only audible from the stream the app says it is showing,
  // and this view shows a peer's screen with no click involved — so it has to
  // declare it. Without this the DM call would be the one place where a share
  // is visible and silent.
  const watchedUserId = remoteScreenEntry?.[0] ?? null;
  const setWatchingUserId = useVoiceUiStore((s) => s.setWatchingUserId);
  useEffect(() => {
    if (!watchedUserId) return;
    setWatchingUserId(watchedUserId);
    // Hand the flag back on unmount, or the channel grid would open thinking
    // you are still watching whoever was on the call.
    return () => setWatchingUserId(null);
  }, [watchedUserId, setWatchingUserId]);

  const title = dmChannel?.name || (participants.length > 1 ? t('call.title') : t('call.calling'));
  const canShareScreen = !isNativeApp() && typeof navigator.mediaDevices?.getDisplayMedia === 'function';

  return (
    <div className="fixed bottom-24 right-4 z-50 flex max-h-[calc(100dvh-7rem)] w-[calc(100vw-2rem)] max-w-80 flex-col overflow-hidden rounded-xl border border-separator bg-surface shadow-overlay" role="region" aria-label={title}>
      <div className="flex shrink-0 items-center justify-between border-b border-separator px-4 py-2.5">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-foreground">{title}</div>
          <div className="text-[11px] text-success">
            {/* tabular-nums: a ticking mm:ss reflows on every digit change without it */}
            <span className="tabular-nums">
              {connecting ? t('call.connecting') : t('call.inCall', { time: fmt(Date.now() - startRef.current) })}
            </span>
          </div>
        </div>
      </div>

      <div className="min-h-0 overflow-y-auto">
        {voiceError && (
          <div className="px-4 pt-3 text-xs text-danger" role="alert">
            {voiceError}
          </div>
        )}

        {remoteScreenEntry && (
          <div className="aspect-video w-full bg-black">
            <video ref={screenVideoRef} autoPlay playsInline className="h-full w-full object-contain" />
          </div>
        )}

        <div className="flex flex-wrap gap-3 p-4">
          {participants.length === 0 && (
            <div className="w-full py-4 text-center text-xs text-muted">{t('call.waiting')}</div>
          )}
          {participants.map((p) => {
            const label = participantLabel(membersById.get(p.userId), p.userId);
            const avatarUrl = membersById.get(p.userId)?.user.avatarUrl ?? null;
            const speaking = speakingMap.get(p.userId) === true && !p.selfMute && !p.serverMute;
            return (
              <div key={p.userId} className="flex w-[88px] flex-col items-center gap-1.5">
                <div
                  className={
                    'relative grid size-16 place-items-center overflow-hidden rounded-full bg-default text-lg font-semibold text-foreground ring-2 transition-colors ' +
                    (speaking ? 'ring-success' : 'ring-transparent')
                  }
                >
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    label.charAt(0).toUpperCase()
                  )}
                  {micShownOff(p) && (
                    <span className="absolute bottom-0 right-0 grid size-5 place-items-center rounded-full bg-danger text-white">
                      <MicOffIcon className="size-3" />
                    </span>
                  )}
                  {(p.selfDeaf || p.serverDeaf) && (
                    <span className="absolute bottom-0 left-0 grid size-5 place-items-center rounded-full bg-danger text-white">
                      <HeadphonesOffIcon className="size-3" />
                    </span>
                  )}
                </div>
                <span className="w-full truncate text-center text-[11px] text-ink">{label}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-center gap-2 border-t border-separator px-4 py-3">
        <CallBtn variant={muted ? 'danger' : 'default'} pressed={muted} title={muted ? t('userWidget.micToggleOn') : t('userWidget.micToggleOff')} onClick={() => toggleMute()}>
          {muted ? <MicOffIcon className="size-5" /> : <MicIcon className="size-5" />}
        </CallBtn>
        <CallBtn variant={deafened ? 'danger' : 'default'} pressed={deafened} title={deafened ? t('userWidget.deafToggleOn') : t('userWidget.deafToggleOff')} onClick={() => toggleDeaf()}>
          {deafened ? <HeadphonesOffIcon className="size-5" /> : <HeadphonesIcon className="size-5" />}
        </CallBtn>
        {canShareScreen && (
          <CallBtn variant={localScreenOn ? 'accent' : 'default'} pressed={localScreenOn} title={localScreenOn ? t('voiceBar.stopShare') : t('voiceBar.share')} onClick={() => void toggleScreen()}>
            <ScreenShareIcon className="size-5" />
          </CallBtn>
        )}
        <Tooltip label={t('call.endCall')}>
          <button
            type="button"
            aria-label={t('call.endCall')}
            onClick={() => endActiveCall()}
            className="grid size-10 place-items-center rounded-full bg-danger text-white transition-transform hover:scale-105"
          >
            <PhoneOffIcon className="size-5" />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}

function CallBtn(props: {
  variant: 'default' | 'danger' | 'accent';
  pressed: boolean;
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip label={props.title}>
      <button
        type="button"
        aria-label={props.title}
        aria-pressed={props.pressed}
        onClick={props.onClick}
        className={
          'grid size-10 place-items-center rounded-full transition-colors ' +
          (props.variant === 'danger'
            ? 'voice-dock-danger'
            : props.variant === 'accent'
              ? 'voice-dock-accent'
              : 'bg-surface-secondary text-ink hover:bg-default')
        }
      >
        {props.children}
      </button>
    </Tooltip>
  );
}
